import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DetailsInformation from './DetailsInformation';
import ProgressDetails from './ProgressDetails';
import { SampleResultSchema } from '@/zod_schemas';
import { type SampleSearchResult } from '@/api/sample/types';

interface ResultDetailsProps {
  selectedSample: SampleSearchResult | null;
  resultData: SampleResultSchema[];
  searchCompleted: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

const ResultDetails = ({
  selectedSample,
  resultData,
  searchCompleted,
  isLoading,
  isError,
  onRetry,
}: ResultDetailsProps) => {
  const navigate = useNavigate();

  // Legacy experiments have populated `outputs[]` from the AWS-Batch result
  // chain and view results via the 5-step variant-analysis stepper at
  // /result/:outputID. Flowgate (universal-runner) experiments have no
  // legacy outputs; their results live behind /flowgate/results/ keyed by
  // experiment id and are shown via the new Flowgate results page.
  const viewResults = (result: SampleResultSchema) => {
    if (result.outputs && result.outputs.length > 0) {
      navigate(`/result/${result.outputs[0].id}`);
    } else {
      navigate(`/result/experiment/${result.id}`);
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      {!selectedSample && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, textAlign: 'center' }}
        >
          <Typography variant="h3" sx={{ mb: 0.5 }}>
            Select a sample
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a registry row to review its linked experiments, analysis
            progress, and available results.
          </Typography>
        </Paper>
      )}

      {selectedSample && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 0.75,
          }}
        >
          <Stack spacing={0.25}>
            <Typography variant="h3">Linked experiments</Typography>
            <Typography variant="body2" color="text.secondary">
              Analysis activity associated with Sample ID{' '}
              <strong>{selectedSample.sample_id}</strong>.
            </Typography>
          </Stack>
          {searchCompleted && resultData.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {resultData.length} experiment
              {resultData.length === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
      )}

      {isLoading && (
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
          <Stack alignItems="center" spacing={1.25}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading linked experiments…
            </Typography>
          </Stack>
        </Paper>
      )}

      {isError && selectedSample && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Linked experiments could not be loaded. Please try again.
        </Alert>
      )}

      {!isLoading &&
        !isError &&
        selectedSample &&
        searchCompleted &&
        resultData.length === 0 && (
          <Paper
            variant="outlined"
            sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, textAlign: 'center' }}
          >
            <Typography variant="h3" sx={{ mb: 0.5 }}>
              No linked experiments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This sample has no experiments available to your account.
            </Typography>
          </Paper>
        )}

      {!isLoading &&
        !isError &&
        resultData?.map((result) => (
          <Paper
            key={result.id}
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
            }}
          >
            <Grid container spacing={2.5} alignItems="stretch">
              <Grid item xs={12} md={3}>
                <DetailsInformation result={result} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ProgressDetails result={result} />
              </Grid>
              <Grid item xs={12} md={3}>
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{
                    height: '100%',
                    justifyContent: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Button
                    onClick={() => viewResults(result)}
                    variant="contained"
                    fullWidth
                  >
                    View Results
                  </Button>
                  <Button variant="outlined" fullWidth disabled>
                    More Actions
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        ))}
    </Stack>
  );
};

export default ResultDetails;
